import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Intercom, { show, hide, onHide, showNewMessage, shutdown } from '@intercom/messenger-js-sdk';
import { useCallback, useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';
import { AccountStatus } from 'api/account';
import { UserId, UserType } from 'api/models';
import { INTERCOM_APP_ID } from 'config/environment';
import { useAccount } from 'hooks/use-account';
import { useUser } from 'hooks/use-user';
import { useUserMetaDataMutation } from 'queries/account';
import { useSubscriptionQuery } from 'queries/billing';
const WidgetStyle = createGlobalStyle`
  #fc_frame.rl-custom-fc-widget.fc_l2r,
  #fc_frame.fc_l2r {
    left: ${({ left })=>`${left}px`} !important;
  }

  #fc_frame.rl-custom-fc-widget.h-open-container.rl-custom-fc-widget--open {
    opacity: 1;
    visibility: visible;
  }
  #fc_frame.widget-open-animate, #fc_frame.fc-widget-normal.widget-open-animate {
    transform-origin: left bottom !important;
  }
`;
var Status = /*#__PURE__*/ function(Status) {
    Status["Unchecked"] = "Unchecked";
    Status["NotRequired"] = "NotRequired";
    Status["ScriptTagAdded"] = "ScriptTagAdded";
    Status["Loaded"] = "Loaded";
    Status["Initialized"] = "Initialized";
    Status["Destroyed"] = "Destroyed";
    Status["Hidden"] = "Hidden";
    Status["Visible"] = "Visible";
    return Status;
}(Status || {});
var SourceForIntercom = /*#__PURE__*/ function(SourceForIntercom) {
    SourceForIntercom["InProduct"] = "in-app";
    return SourceForIntercom;
}(SourceForIntercom || {});
var AccountStatusForIntercom = /*#__PURE__*/ function(AccountStatusForIntercom) {
    AccountStatusForIntercom["Trial"] = "Trial";
    AccountStatusForIntercom["Paid"] = "Paid";
    return AccountStatusForIntercom;
}(AccountStatusForIntercom || {});
let status = "Unchecked";
let _openChat = null;
export const openIntercomChat = (message)=>{
    if (!_openChat) return false;
    _openChat(message);
    return true;
};
/** Same gate as the left-nav chat icon — Native/Partner, or External on allowlisted hosts. */ export const canShowIntercomChat = (userType)=>{
    const EXTERNAL_DOMAIN_LIST = [
        'success.rocketlane.com',
        'localhost:3000'
    ];
    const { host } = window.location;
    return userType === UserType.Native || userType === UserType.Partner || userType === UserType.External && EXTERNAL_DOMAIN_LIST.includes(host);
};
export const useIsIntercomChatAvailable = ()=>{
    const { user } = useUser();
    return canShowIntercomChat(user.userType);
};
export const IntercomWidget = ({ left = 50, render })=>{
    var _userMeta_metaData, _subscriptionData_data_plan, _subscriptionData_data;
    const { user, userMeta } = useUser();
    const { userId } = user;
    const restoreId = userMeta === null || userMeta === void 0 ? void 0 : (_userMeta_metaData = userMeta.metaData) === null || _userMeta_metaData === void 0 ? void 0 : _userMeta_metaData.freshchatRestoreId;
    const { status: accountStatus } = useAccount();
    const { mutateAsync: updateUserMetaData } = useUserMetaDataMutation(UserId(userId));
    const subscriptionData = useSubscriptionQuery();
    const loadWidget = canShowIntercomChat(user.userType);
    const initIntercom = useCallback((open = false)=>{
        var _user_role, _user_role1, _subscriptionData_data_plan, _subscriptionData_data, _user_company, _user_company1;
        status = "Initialized";
        Intercom({
            app_id: INTERCOM_APP_ID !== null && INTERCOM_APP_ID !== void 0 ? INTERCOM_APP_ID : '',
            email: user.emailId,
            alignment: 'left',
            name: `${user.firstName} ${user.lastName}`,
            user_id: userId.toString(),
            source: "in-app",
            Subscription: accountStatus === AccountStatus.Trial ? "Trial" : "Paid",
            role: user === null || user === void 0 ? void 0 : (_user_role = user.role) === null || _user_role === void 0 ? void 0 : _user_role.roleName,
            roleType: user === null || user === void 0 ? void 0 : (_user_role1 = user.role) === null || _user_role1 === void 0 ? void 0 : _user_role1.type,
            Plan: subscriptionData === null || subscriptionData === void 0 ? void 0 : (_subscriptionData_data = subscriptionData.data) === null || _subscriptionData_data === void 0 ? void 0 : (_subscriptionData_data_plan = _subscriptionData_data.plan) === null || _subscriptionData_data_plan === void 0 ? void 0 : _subscriptionData_data_plan.planName,
            horizontal_padding: 70,
            vertical_padding: 35,
            hide_default_launcher: true,
            companies: [
                {
                    company_id: user === null || user === void 0 ? void 0 : (_user_company = user.company) === null || _user_company === void 0 ? void 0 : _user_company.companyId,
                    name: user === null || user === void 0 ? void 0 : (_user_company1 = user.company) === null || _user_company1 === void 0 ? void 0 : _user_company1.companyName
                }
            ]
        });
        onHide(()=>{
            status = "Hidden";
        });
        if (open) {
            show();
        }
    }, [
        accountStatus,
        subscriptionData === null || subscriptionData === void 0 ? void 0 : (_subscriptionData_data = subscriptionData.data) === null || _subscriptionData_data === void 0 ? void 0 : (_subscriptionData_data_plan = _subscriptionData_data.plan) === null || _subscriptionData_data_plan === void 0 ? void 0 : _subscriptionData_data_plan.planName,
        user,
        userId
    ]);
    const destroyIntercom = ()=>{
        shutdown();
        status = "Destroyed";
    };
    const initialize = useCallback(()=>{
        if (status !== "Unchecked" && status !== "Destroyed") {
            return;
        }
        if (status === "Destroyed" || status === "Unchecked") {
            status = "Loaded";
            if (userId && restoreId) {
                initIntercom();
            }
        }
    }, [
        initIntercom,
        restoreId,
        userId
    ]);
    useEffect(()=>{
        return ()=>{
            destroyIntercom();
        };
    }, []);
    useEffect(()=>{
        if (status === "Unchecked" || status === "Destroyed") {
            if (loadWidget) {
                initialize();
            } else {
                status = "NotRequired";
            }
        }
    }, [
        accountStatus,
        loadWidget,
        user,
        userMeta,
        updateUserMetaData,
        initialize
    ]);
    const openIntercomWidget = ()=>{
        if (status === "Hidden" || status === "Initialized") {
            status = "Visible";
            show();
        } else if (status === "Visible") {
            status = "Hidden";
            hide();
        } else {
            initIntercom(true);
        }
    };
    useEffect(()=>{
        if (!loadWidget) return;
        _openChat = (message)=>{
            const isBooted = status === "Initialized" || status === "Hidden" || status === "Visible";
            if (!isBooted) {
                initIntercom(false);
            }
            status = "Visible";
            if (message) {
                showNewMessage(message);
            } else {
                show();
            }
        };
        return ()=>{
            _openChat = null;
        };
    }, [
        initIntercom,
        loadWidget
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: loadWidget && /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(WidgetStyle, {
                    left: left
                }),
                render(openIntercomWidget)
            ]
        })
    });
};
